import useTransactionStore from "../stores/useTransactionStore";

/**
 * cartIdentityGuard — stale cart identity (sOrderID) protection.
 *
 * A reload can restore a pre-checkout cart snapshot whose sOrderID was already
 * consumed by a completed order: useCartStore persists through sqliteStorage,
 * which debounces writes by 100ms, so the post-checkout ID reset may never be
 * flushed before a refresh/crash/PWA update. Reusing a consumed sOrderID is
 * destructive twice over:
 *   1. db.orders is keyed on sOrderID, so the new sale silently overwrites the
 *      earlier order locally.
 *   2. The backend dedupes on sOrderID — it returns "200" without creating a
 *      record, the POS marks the order isSynced=1, and the bill is lost
 *      server-side while appearing synced locally.
 * (Production incident: HSN/103148 and HSN/103176 shared one sOrderID.)
 *
 * A resurrected stale snapshot can carry BOTH a matching documentno and a
 * matching sOrderID (RTN/100614→100618: the reload restored the whole cart,
 * not just one field), which defeats a documentno-only comparison — the
 * incoming sale looked like a legitimate retry even though its content (total,
 * item count) was entirely different. shouldRegenerateIdentityAtSave verifies
 * content matches before trusting a documentno match for that reason.
 *
 * Two decision helpers, one per defense layer. ID regeneration itself stays at
 * the call sites so each layer controls exactly which IDs are safe to reissue.
 */

/**
 * True when a documentno belongs to a parked series — parked bills ("PB/") or
 * pre-orders/advance orders (till-configured prefix, e.g. "ADV/"). A retrieved
 * cart legitimately shares its sOrderID with a db.orders row ONLY while that
 * row still carries its parked-series number; once the row holds a regular
 * bill number the parked bill was already completed, and any cart still
 * claiming its identity is a stale snapshot.
 */
export const isParkedSeriesDocumentno = (documentno, preorderPrefix) => {
  if (!documentno) return false;
  if (documentno.startsWith("PB/")) return true;
  if (preorderPrefix && documentno.startsWith(preorderPrefix)) return true;
  return false;
};

/**
 * Save-time (DI-33) check — used in processOrderApproved after documentno is
 * resolved and before any DB write, and in syncOrders' fresh-order branch
 * before the online POST. Returns true when the order about to be saved/posted
 * would overwrite a DIFFERENT existing order — i.e. same sOrderID but a
 * different documentno, and not a flow that legitimately reuses its identity.
 *
 * Same-documentno matches are NOT stale: that is the same order being saved
 * again (e.g. a checkout retry after a partial failure) and regenerating there
 * would duplicate the sale.
 */
export const shouldRegenerateIdentityAtSave = ({ existingOrder, cart, documentno, preorderPrefix }) => {
  if (!existingOrder) return false;
  if (existingOrder.documentno === documentno) {
    // Same documentno normally proves "this exact order being re-saved" (e.g. a
    // checkout retry after a partial failure) — legitimate, no regeneration needed.
    // But a reload/crash can resurrect a stale pre-checkout cart snapshot whose
    // documentno AND sOrderID both revert together (RTN/100614→100618 incident:
    // both identifiers matched, yet the sale was a different customer/₹595/4 items,
    // not a retry of the ₹510/2-item sale already saved under that identity).
    // A genuine retry re-submits identical content, so require total + item count
    // to match before trusting the documentno match; otherwise fall through to the
    // same flow-specific exemptions below (Layaway/split/OCU/credit/parked-retrieve
    // legitimately reuse an identity with changing content and stay exempt).
    // `|| 0` normalizes a missing/undefined total on either side before comparing —
    // without it, Number(undefined) === Number(undefined) is NaN === NaN, which is
    // always false, so two carts that both simply lack a .total (e.g. a minimal/
    // synthetic cart) would incorrectly fail this match and fall through to the
    // unconditional `return true` below, misclassifying a legitimate same-order
    // retry as a stale identity.
    const sameTotal = Number(cart?.total || 0) === Number(existingOrder.total || 0);
    const sameItemCount = (cart?.items?.length || 0) === (existingOrder.items?.length || 0);
    if (sameTotal && sameItemCount) return false;
    // TSK-2935: a Prepare-Bill-confirmed order's sOrderID is the only legitimate
    // identity for the rest of its lifecycle — completing it (discount may have
    // changed the total) or cancelling it (total zeroes out) both legitimately
    // change total/item count from what Prepare Bill originally saved, so the
    // content-match check above doesn't apply here. documentno already matching
    // (this if-block) plus preparedBillOrigin confirms this is that same order,
    // not an unrelated stale snapshot. Confirmed via production trace: cancelling
    // a Prepare-Bill order (BHRS/1781) tripped this exact false positive and
    // orphaned its confirmed sOrderID mid-cancel.
    if (existingOrder.preparedBillOrigin === "Y") return false;
  }
  if (cart?.type === "Layaway" || cart?.layAway === "Y" || cart?.docType === "Advance") {
    // Layaway close/re-save legitimately reuses this sOrderID only while the
    // existing row is still the open parked-series (advance) record. A cart
    // that never flushed its post-checkout reset can still carry a stale
    // type: "Layaway"/layAway: "Y"/docType: "Advance" flag after this identity
    // was already closed under a different documentno — trusting the flag
    // alone let a later, unrelated save silently overwrite a completed sale
    // (production: S1S/39378 -> CS1S CAN/4, 2026-07-28 — this exact sOrderID
    // had closed 3 minutes earlier). Require the row to still be parked
    // before exempting.
    //
    // BUG-1914: some cart shapes carry layAway/docType without cart.type ever
    // being set (isStalePersistedCart below already ORs cart.layAway alongside
    // cart.type for this exact reason — this brings the save-time check in
    // line with it). NOTE: audited against 3 real Chocolala production orders
    // (AJ002/25537, AJ002/25538, AJ2 CAN/9) on 2026-08-13 — all three already
    // carried cart.type === "Layaway" in addition to layAway/docType, so the
    // old type-only check would have handled them correctly too; this specific
    // gap (type absent, only layAway/docType present) has NOT been confirmed
    // against a real incident. Kept as defensive hardening, matching the
    // layAway/docType-as-alternative pattern already used elsewhere
    // (usePaymentFlow.js, useSuperMarketActions.js), not as a proven fix.
    //
    // docType: "Advance" is included too — usePaymentFlow.js and
    // useSuperMarketActions.js already treat it as an independent alternative
    // to layAway ("isAdvanceOrder = layAway==='Y' || docType==='Advance'"),
    // so a cart can reach this guard with only docType set. Safe to fold into
    // this branch: the isParkedSeriesDocumentno gate below still requires the
    // existing row to be genuinely open before exempting, so a stale/leftover
    // docType flag (useOrderProcessing.js's own comment above ~line 1616 notes
    // it can be a leftover from an abandoned retrieval) still can't resurrect
    // an already-closed order — same protection layAway/type already get.
    //
    // GPR-live incident (2026-08-28): isParkedSeriesDocumentno's preorderPrefix
    // comes from cwr_pos_sequence_rule, confirmed EMPTY in every production/test
    // DB checked (gpr-live, gpr_test, chocolala_live) — so for any advance-order
    // documentno that isn't literally "PB/"-prefixed, that check always returns
    // false, meaning this branch UNCONDITIONALLY regenerated on every single
    // advance-order completion/cancel, not just the intended stale-snapshot case.
    // Confirmed live: RDGPOS/4229, GDPPOS/8819 (gpr-live), RAK2 ADV/129, CBA
    // ADV/69 (chocolala_live) all stuck in cwr_pos_errors with an FK violation
    // referencing an sOrderID that never reached s_order, because the genuine
    // completion minted a fresh ID instead of reusing the retrieved order's own.
    //
    // Fix: trust a same-order match captured directly at retrieval time
    // (cart.retrievedDocumentno, stamped once by the retrieval entry point —
    // selectParkedBill/selectLayAwayOrder/selectCompleteLayAwayOrder/handleRetrieve/
    // handleRetrieveEstimate) instead of re-deriving "is this still parked" from
    // a prefix string that depends on unpopulated config. If the row's CURRENT
    // documentno still matches what this exact cart saw when it was retrieved,
    // nothing else has touched this identity since — safe to keep reusing it,
    // regardless of what the row's own prefix looks like. If it doesn't match,
    // this identity was already closed/renumbered by another save — genuinely
    // stale, must regenerate. Falls back to the prefix check (still correct and
    // config-independent for "PB/") when no retrieval snapshot is present, so a
    // cart that hasn't gone through an updated retrieval path degrades to
    // today's existing (safe, if overly aggressive) behavior instead of a new
    // unguarded exemption.
    if (cart?.retrievedDocumentno && existingOrder.documentno === cart.retrievedDocumentno) return false;
    return !isParkedSeriesDocumentno(existingOrder.documentno, preorderPrefix);
  }
  if (cart?.isSplit === "Y" || cart?.statusType === "OCU" || cart?.creditSale === "Y" || cart?.isCreditSale === "Y") {
    // Unlike Layaway (which deliberately transitions from a parked-series number
    // to a new regular one), these flows resolve to the SAME documentno on every
    // save throughout their lifecycle — a non-PB/ADV cart just keeps cart.documentno
    // as-is (see resolvedDocumentNo above, ~line 495). So a genuine incremental
    // re-save of the SAME order always matches existingOrder.documentno exactly;
    // if it doesn't, this identity was already consumed by a different, completed
    // order and the flag alone must not be trusted blindly (same root cause as
    // the Layaway gap above — a stale isSplit/OCU/creditSale flag surviving on
    // an unrelated new cart would otherwise be exempted just as silently).
    // BUG-2338: this only checked cart.isCreditSale, a field no real cart ever
    // carries (every actual assignment is cart.creditSale — SMPaymentBar.js, the
    // order classifier in useOrderProcessing.js, etc.) — so a genuine credit-sale
    // cart never took this safe documentno-direct-compare branch and fell through
    // to the isRetrived/broken-prefix path below whenever retrievedDocumentno was
    // also unset, unconditionally regenerating sOrderID. Added cart.creditSale
    // alongside the original cart.isCreditSale (kept, not removed) in case some
    // other caller does set that field — the OR costs nothing since this branch's
    // own exemption is already the safe direct documentno compare, not a blind trust.
    return !!existingOrder.documentno && existingOrder.documentno !== documentno;
  }
  if (cart?.isRetrived === "Y") {
    // Retrieved PB/ADV bill completing under a new regular number — legitimate
    // reuse, but only while the existing row is still the parked one. If the
    // row already carries a regular bill number, that completion already
    // happened and this cart is a stale snapshot of it.
    // Same retrieval-snapshot trust as the Layaway branch above — see its
    // comment for the full rationale (GPR-live incident, 2026-08-28).
    if (cart?.retrievedDocumentno && existingOrder.documentno === cart.retrievedDocumentno) return false;
    return !isParkedSeriesDocumentno(existingOrder.documentno, preorderPrefix);
  }
  return true;
};

const firedLines = (items) => (items || []).filter((i) => i?.fbOrderLineId && i.isCanceled !== "Y");

/**
 * BUG-2425 (round 3): a prepared bill and the cart are the same table session when they
 * share fired lines (fbOrderLineId is unique per session). The bill row keeps the table
 * Prepare Bill was clicked on, so after Transfer Bill its tableId is the OLD table — a
 * table compare missed it and the moved table was billed and paid as a second order
 * (HMRS/10875, 10881, 10882, 10892; preprod HMRS/2722 → 2723). Split tables keep the
 * table compare: their child carts carry a derived tableId and only a subset of lines.
 */
const sameTableSession = (bill, cart) =>
  bill.tableId === cart.tableId || (bill.isSplit !== "Y" && !bill.splitTable && cart.isSplit !== "Y" && !cart.splitTable);

/**
 * BUG-2425: the open (unpaid, not cancelled) Prepare-Bill order this dine-in cart
 * continues, when the cart has lost its preparedBillOrigin/identity (reload wipe,
 * stale table snapshot, sale-type switch, KOT fire minting a new id). Matched on
 * the table and on fbOrderLineId: every fired line of that bill must still be in
 * the cart, uncancelled and at the same weight, which only the same table session
 * can satisfy. A bill whose lines were cancelled or changed is not matched: the CW
 * confirm keeps the lines Prepare Bill saved, so it would invoice them. Returns the
 * order only when exactly one matches; otherwise null (cart left as is).
 */
export const findOpenPreparedBillForCart = ({ cart, orders }) => {
  if (!cart?.tableId || cart.preparedBillOrigin === "Y") return null;
  const cartLines = new Map(firedLines(cart.items).map((i) => [i.fbOrderLineId, i]));
  if (cartLines.size === 0) return null;
  const matches = (orders || []).filter((o) => {
    if (!o || o.sOrderID === cart.sOrderID || o.preparedBillOrigin !== "Y" || !sameTableSession(o, cart)) return false;
    if (Number(o.paid) > 0 || !(Number(o.total) > 0) || !o.documentno) return false;
    const billLines = firedLines(o.items);
    return (
      billLines.length > 0 &&
      billLines.every((l) => cartLines.has(l.fbOrderLineId) && Number(cartLines.get(l.fbOrderLineId).weight) === Number(l.weight))
    );
  });
  return matches.length === 1 ? matches[0] : null;
};

/**
 * BUG-2425 trace: the open prepared bills on this cart's table that findOpenPreparedBillForCart
 * did NOT pick, each with why — so a second order on a table that already has one is explainable.
 */
export const explainOpenPreparedBillsOnTable = ({ cart, orders }) => {
  if (!cart?.tableId) return [];
  const cartAll = new Map((cart.items || []).filter((i) => i?.fbOrderLineId).map((i) => [i.fbOrderLineId, i]));
  return (orders || [])
    .filter(
      (o) =>
        o &&
        o.sOrderID !== cart.sOrderID &&
        o.preparedBillOrigin === "Y" &&
        (o.tableId === cart.tableId || (sameTableSession(o, cart) && (o.items || []).some((l) => l?.fbOrderLineId && cartAll.has(l.fbOrderLineId))))
    )
    .filter((o) => !(Number(o.paid) > 0) && Number(o.total) > 0)
    .map((o) => {
      const lines = firedLines(o.items);
      const missing = lines.filter((l) => !cartAll.has(l.fbOrderLineId)).length;
      const cancelled = lines.filter((l) => cartAll.get(l.fbOrderLineId)?.isCanceled === "Y").length;
      const qtyChanged = lines.filter((l) => {
        const c = cartAll.get(l.fbOrderLineId);
        return c && c.isCanceled !== "Y" && Number(c.weight) !== Number(l.weight);
      }).length;
      return { sOrderID: o.sOrderID, documentno: o.documentno, total: o.total, lines: lines.length, missing, cancelled, qtyChanged };
    });
};

/** BUG-2425: the cart carrying that prepared bill's identity, as Prepare Bill itself stamps it. */
export const adoptPreparedBill = (cart, bill) => ({
  ...cart,
  sOrderID: bill.sOrderID,
  documentno: bill.documentno,
  preparedBillOrigin: "Y",
  isPrepareBillCreate: "N",
  layAway: "N",
});

/**
 * BUG-2425: id for a cart re-based on a sale-type switch. A prepared bill keeps the
 * sOrderID Prepare Bill created in CW (payment confirms that id); anything else gets `newId`.
 */
export const keepPreparedBillSOrderID = (cart, newId) =>
  cart?.preparedBillOrigin === "Y" && cart?.sOrderID ? cart.sOrderID : newId;

/**
 * BUG-2425: true when `existingOrder` is a Prepare-Bill order that has not been settled
 * yet (unpaid, not zeroed by a cancel) and `cart` is that same open bill — same
 * documentno, cart still flagged preparedBillOrigin. Such a cart is the live
 * continuation of the order, never a stale snapshot.
 */
export const isOpenPreparedBill = ({ cart, existingOrder }) =>
  !!existingOrder &&
  cart?.preparedBillOrigin === "Y" &&
  existingOrder.preparedBillOrigin === "Y" &&
  !!existingOrder.documentno &&
  existingOrder.documentno === cart?.documentno &&
  !(Number(existingOrder.paid) > 0) &&
  Number(existingOrder.total) > 0;

/**
 * BUG-2425: identity to use when a table's cart is rebuilt from its fbOrderData snapshot.
 * TSK-2935's override restored the Prepare-Bill number only when the snapshot's sOrderID
 * matched a db.orders row. A snapshot can carry the sOrderID of an earlier KOT fire while
 * naming the prepared bill's documentno (Himayathnagar G5: wsData sOrderID 7B61EA9A…, which
 * exists nowhere, documentno HMRS/10439). The rebuilt cart then lost preparedBillOrigin, the
 * next KOT fire minted a new sOrderID + number, and Prepare Bill created a second order,
 * leaving the first un-invoiced (HMRS/10547→10548 … 10620→10621). When the snapshot's
 * sOrderID matches no order, adopt the single open prepared bill carrying the snapshot's
 * documentno (same table when both know it). Anything else returns the cart unchanged.
 */
export const resolvePreparedBillIdentity = async ({ cartData, getOrder, findOrdersByDocumentno }) => {
  if (!cartData?.sOrderID) return cartData;
  const existingOrder = await getOrder(cartData.sOrderID);
  if (existingOrder?.preparedBillOrigin === "Y") {
    return { ...cartData, documentno: existingOrder.documentno, preparedBillOrigin: "Y" };
  }
  if (existingOrder || !cartData.documentno || typeof findOrdersByDocumentno !== "function") return cartData;
  const sameNumber = (await findOrdersByDocumentno(cartData.documentno)) || [];
  const openPrepared = sameNumber.filter(
    (o) =>
      o?.preparedBillOrigin === "Y" &&
      !(Number(o.paid) > 0) &&
      Number(o.total) > 0 &&
      (!cartData.tableId || !o.tableId || o.tableId === cartData.tableId)
  );
  if (openPrepared.length !== 1) return cartData;
  const preparedBill = openPrepared[0];
  return { ...cartData, sOrderID: preparedBill.sOrderID, documentno: preparedBill.documentno, preparedBillOrigin: "Y" };
};

/**
 * Mount-time (BUG-1477) check — used in useCoreEffects after fetching the
 * db.orders row matching the persisted cart's sOrderID. Returns true when the
 * persisted cart is a stale snapshot of an already-saved order and must be
 * cleared. Layaway and confirmed-restaurant carts intentionally share their
 * sOrderID with a db.orders row; retrieved carts do too, but only while that
 * row still carries its parked-series documentno (see isParkedSeriesDocumentno).
 */
export const isStalePersistedCart = ({ cart, existingOrder, preorderPrefix }) => {
  if (!existingOrder) return false;
  // BUG-2425: an open Prepare Bill legitimately shares its sOrderID with the db.orders
  // row Prepare Bill just created, and that row carries a regular till number (MR tills
  // have no parked/pre-order series). Prepare Bill's success branch leaves the cart at
  // layAway "N" / statusType "N", so every check below judged it stale and wiped it on
  // the next POS mount — the table was then re-billed as a new order and the prepared
  // bill was left un-invoiced in CW (KPRS/8610, KMRS/7660). Keep it while the row is
  // still that same, unsettled prepared bill; once it is paid or cancelled (zeroed),
  // the normal rules below apply again.
  if (isOpenPreparedBill({ cart, existingOrder })) return false;
  if (cart?.layAway === "Y" || cart?.type === "Layaway") {
    // Same identity-trust gap as shouldRegenerateIdentityAtSave above: a
    // Layaway cart is only legitimately unfinished while its row is still
    // the parked (advance) record. If the row already shows a different,
    // already-closed documentno, this persisted cart is stale and must not
    // survive reload to potentially overwrite that closed sale later.
    // Retrieval-snapshot trust (see shouldRegenerateIdentityAtSave's Layaway
    // branch, GPR-live incident 2026-08-28) applies here too.
    if (cart?.retrievedDocumentno && existingOrder.documentno === cart.retrievedDocumentno) return false;
    return !isParkedSeriesDocumentno(existingOrder.documentno, preorderPrefix);
  }
  if (cart?.statusType === "OCU") {
    // Same reasoning as shouldRegenerateIdentityAtSave's isSplit/OCU/isCreditSale
    // branch: this flow's documentno never changes across its lifecycle, so a
    // persisted cart genuinely continuing the same order always still matches the
    // row's current documentno. A mismatch means the row moved on since this cart
    // was last flushed to disk — stale, must not survive reload.
    return !!existingOrder.documentno && existingOrder.documentno !== cart?.documentno;
  }
  if (cart?.isRetrived === "Y") {
    if (cart?.retrievedDocumentno && existingOrder.documentno === cart.retrievedDocumentno) return false;
    return !isParkedSeriesDocumentno(existingOrder.documentno, preorderPrefix);
  }
  return true;
};

/**
 * DB-layer backstop (last line of defense) — called from both DexieAdapter and
 * SQLiteAdapter's Table.put() for the "orders" table only. Every known local
 * write path (checkout, cancel/void, discard-parked-bill) already calls
 * shouldRegenerateIdentityAtSave at its own call site before saving; this exists
 * so a FUTURE call site that forgets that check still can't silently overwrite a
 * completed sale — the destructive write simply cannot reach the table.
 *
 * Confirmed necessary in production: VRP/101112, a real ₹326.40 sale, was
 * overwritten by a ₹0 CAN/319 cancel record sharing its sOrderID (clearCart had
 * no identity check at all before this was added).
 *
 * Exempt when data.isSynced is 1/"1" — that is the existing convention (see
 * useOrderProcessing.js's `if (!isSynced)` guard, exportSequenceDiagnostics'
 * unsyncedOrders filter) for "this write is a trusted, server-confirmed record,"
 * e.g. useWebSocketHandler.js's online-order upsert, which legitimately
 * re-writes the same sOrderID on every status update and must not be touched.
 *
 * @param {Object} data - the row about to be written (mutated in place if regenerated)
 * @param {(sOrderID: string) => Promise<Object|undefined>} getExistingOrder
 * @param {(entry: Object) => void} [logAuditEntry] - best-effort, non-blocking
 */
export async function guardOrderIdentityBeforeWrite(data, getExistingOrder, logAuditEntry) {
  if (!data || !data.sOrderID) return;
  if (data.isSynced === 1 || data.isSynced === "1") return;
  try {
    const existingOrder = await getExistingOrder(data.sOrderID);
    if (!existingOrder) return;

    let preorderPrefix = null;
    try {
      const { default: useSessionStore } = await import("../stores/useSessionStore");
      preorderPrefix = useSessionStore.getState().preorderSequence?.prefix;
    } catch {
      // best-effort only — absence just means the parked/pre-order exemption is skipped this time
    }

    if (!shouldRegenerateIdentityAtSave({ existingOrder, cart: data, documentno: data.documentno, preorderPrefix })) return;

    const staleSOrderID = data.sOrderID;
    data.sOrderID = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID().replace(/-/g, "").toUpperCase() : `${Date.now()}${Math.random()}`.replace(/\D/g, "").toUpperCase();

    console.warn("[DI-33][DB-LAYER-BACKSTOP] Stale sOrderID caught at write time — a call site is missing its own identity check. Regenerated to prevent order overwrite.", {
      staleSOrderID,
      newSOrderID: data.sOrderID,
      documentno: data.documentno,
      overwrittenDocumentno: existingOrder.documentno,
    });

    logAuditEntry?.({
      event: "stale-identity-recovery",
      documentno: data.documentno,
      staleSOrderID,
      newSOrderID: data.sOrderID,
      existingDocumentno: existingOrder.documentno,
      flow: "db-layer-backstop",
    });
  } catch (err) {
    console.warn("[DI-33][DB-LAYER-BACKSTOP] Guard check failed (non-blocking):", err?.message);
  }
}

/**
 * Estimate-level dedup guard. An estimate/online-order retrieval (scan or
 * manual "Retrieve") carries no client-side lock — nothing marks the source
 * estimate consumed, so the same still-"PE" estimate can be retrieved and
 * billed a second time before the first sale's completion (or the server
 * round-trip that would close the estimate) lands. Each retrieval mints its
 * own cart with a fresh documentno preview, so shouldRegenerateIdentityAtSave
 * (scoped to a single sOrderID slot) doesn't catch this — a second retrieval's
 * documentno mismatch just makes it regenerate a brand-new sOrderID instead of
 * blocking, converting a timing gap into a genuine second order.
 *
 * Production incident: KKPPOS/5612 and KKPPOS/5613, same s_estimate_id, two
 * different sOrderIDs, 5 seconds apart.
 *
 * @param {Array} allOrders - result of db.orders.toArray() (caller-supplied so
 *   a call site that already fetched it — e.g. syncOrders' pending-orders scan
 *   — doesn't pay for a second full-table read)
 * @param {string} estimateId
 * @param {string} excludeSOrderID - the current cart's own sOrderID, so a
 *   legitimate retry/resave of the SAME order is never mistaken for a duplicate
 */
export function findDuplicateEstimateOrder(allOrders, estimateId, excludeSOrderID) {
  if (!estimateId) return null;
  return (allOrders || []).find((o) => o.estimateId === estimateId && o.sOrderID !== excludeSOrderID) || null;
}

/**
 * Issue-7 / FK-3692,6319 — snapshot per-order customer-address + B2B state onto
 * a cart record right before it's written to db.orders (checkout, cancel, and
 * discard-parked-bill all write here — same call-site pattern as
 * shouldRegenerateIdentityAtSave above).
 *
 * syncOrders builds the backend orderJson (sCustomerBillingId,
 * sCustomerShippingId, sCustomerId, b2bOrder) from db.orders rows in a batch
 * loop that can process orders long after they were created. It previously
 * read the LIVE localStorage "customerAddressIds" and useTransactionStore
 * isB2bEnabled at THAT later point — correct only for whichever order happens
 * to match whatever customer/session is active at sync time, and silently
 * wrong for every other order sitting in the queue. That leaks one customer's
 * billing/shipping address id (or B2B status) onto an unrelated order — the
 * confirmed mechanism behind the recurring s_customer_billing_id FK violations
 * (tickets 3692, 6319) and unexpected-s_customer_id Pending Sync failures.
 *
 * Capturing here, at write time, is unconditional (not gated behind
 * disableOfflineBilling or any other config) so it protects both the
 * always-online and offline-queued paths alike. `=== undefined` guards make
 * this idempotent — never overwrites a value already set earlier in this
 * cart's lifecycle (e.g. the /estimate-route customerAddressIds already
 * stamped by customer.js), and skipped entirely for already-synced rows
 * (isSynced 1), which don't need correcting.
 *
 * @param {Object} cartToDb - the row about to be written (mutated in place)
 */
export const captureOrderCreationSnapshot = (cartToDb) => {
  if (!cartToDb) return;
  if (cartToDb.isSynced === 1 || cartToDb.isSynced === "1") return;
  // BUG-2099 follow-up (live-confirmed on KWC preprod, 2026-08-23: till
  // 80B7AC21AD574B6B87DFF918E8DACA47 billed two separate "Default Customer -
  // Uttarakhand" walk-in orders to INDIAN OIL CORPORATION LIMITED's address,
  // minutes after an actual INDIAN OIL B2B order on the same till): this read
  // trusted localStorage["customerAddressIds"] as belonging to THIS cart just
  // because the cart's own customerAddressIds was still unset -- but that's
  // also true for every brand-new cart started right after a DIFFERENT
  // customer's checkout wrote this same key, before that checkout's own
  // cleanup (useOrderProcessing.js's localStorage.removeItem("customerAddressIds"))
  // has run. customer.js's writes (createCustomer/updateCustomer) already stamp
  // an `sOrderID` onto this exact blob for exactly this ownership check --
  // resolveCapturedAddressId's live-read fallback tier (useOrderProcessing.js)
  // already verifies it before trusting the cache. This capture-time read
  // never did, so a stale blob could ride into a brand-new order's "already
  // captured" snapshot and be trusted unconditionally downstream. Require the
  // same sOrderID match here.
  const isOwnedByThisCart = (storedAddressIds) => storedAddressIds?.sOrderID && storedAddressIds.sOrderID === cartToDb.sOrderID;
  if (cartToDb.customer && cartToDb.customer.customerAddressIds === undefined) {
    try {
      const storedAddressIds = JSON.parse(localStorage.getItem("customerAddressIds") || "null");
      if (storedAddressIds && isOwnedByThisCart(storedAddressIds)) cartToDb.customer.customerAddressIds = storedAddressIds;
    } catch (e) {
      /* leave unset; syncOrders falls back to the legacy live read for this order */
    }
  } else if (!cartToDb.customer && cartToDb.customerAddressIds === undefined) {
    // BUG: the branch above required cartToDb.customer to already be a truthy object
    // (it needs somewhere to assign customerAddressIds onto) -- any checkout where
    // cartToDb.customer is unset (e.g. the till's business unit has no configured
    // b2cCustomer default) skipped the snapshot entirely, silently falling straight
    // back to syncOrders' live localStorage read: the exact cross-customer address
    // leak this snapshot exists to close. Snapshot to a top-level field instead of
    // creating a synthetic cartToDb.customer object, so this never changes whether
    // other code treats the order as having a real customer attached.
    try {
      const storedAddressIds = JSON.parse(localStorage.getItem("customerAddressIds") || "null");
      if (storedAddressIds && isOwnedByThisCart(storedAddressIds)) cartToDb.customerAddressIds = storedAddressIds;
    } catch (e) {
      /* leave unset; syncOrders falls back to the legacy live read for this order */
    }
  }
  if (cartToDb.isB2bEnabledSnapshot === undefined) {
    try {
      cartToDb.isB2bEnabledSnapshot = useTransactionStore.getState().isB2bEnabled ?? null;
    } catch (e) {
      /* leave unset; syncOrders falls back to the legacy live read for this order */
    }
  }
  // Same leak, same fix, for selectedReturnType: syncOrders' payments-array gate
  // (useOrderProcessing.js) reads this live to decide whether a return order's
  // `payments` should ship empty (STORE_CREDIT/EXCHANGE, where the refund isn't a
  // real tender) or carry its actual paymentsList (plain REFUND). Read live inside
  // the batch loop, it reflects whatever the till is doing NOW, not what this order
  // actually was when the cashier completed it -- a plain cash Refund left pending
  // (offline gap, prior sync failure) gets its payments silently wiped to [] if the
  // till has since moved on to an Exchange or Store-Credit return, submitting a
  // non-zero order total with zero payment lines. Confirmed live: KWC return
  // JK/26-27/276 (-67980, Cash tendered per metaData) synced with `payments: []`
  // and never got a response from posOrderProcessor1.
  if (cartToDb.selectedReturnTypeSnapshot === undefined) {
    try {
      cartToDb.selectedReturnTypeSnapshot = useTransactionStore.getState().selectedReturnType ?? null;
    } catch (e) {
      /* leave unset; syncOrders falls back to the legacy live read for this order */
    }
  }
};
